import { Button, message, Spin, Table } from "antd";
import { FC, Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { adminAppoveRecords, clearData, adminDeleteRecords, loadCountries, loadDataForApproval, loadSubChannels, getColumns, clearSuccess, clearError } from "../../actions/eComm/eCommCustomersActions";
import { IActiveCustomer, IColumn, ICountry, ICustomerStatus } from "../../reducer/ECommCustomers/_interfaces";
import {
    BOOL_DROPDOWN,
    CODE_HIERARHY,
    COLLABORATIVE_ADS,
    ONSITE_RETAIL_MEDIA,
    SHARED_DATA_WITH_CUSTOMERS,
    DIGITAL_TOOLS_OFFERED,
    CUSTOMER_NAME,
    CUSTOMER_URL,
    DATA_REPORTING_ESTIMATION,
    DIRECT_INDIRECT,
    E_CUSTOMER_TYPE,
    ERED_ERETAIL,
    ERED_FSA,
    ERED_QC, 
    ERED_QSR,
    FREQUENCY_DATA_SHARING,
    MARKET_SHARES,
    MEDIA_HOUSE,
    ORDERING_CHANNEL,
    PFP_IMPLEMENTATION,
    DESCRIBE_PLANNED,
    PRIORITY,
    PRODUCT_SUPPLIER,
    RETAIL_MEDIA,
    SAP_CODE,
    SELL_DATA_MESH,
    SELLES_DATA_SHARING
} from "./constants";
import DropDownCustom from "../../components/dropDownCustom";
import moment from "moment-timezone";

interface ParamTypes {
    id: string;
}

const getCellProps = (record:IActiveCustomer, key:keyof IActiveCustomer) => (
    record.status === 'updated'
        ? {
            className: record[key] !== record.children?.[0]?.[key] ? 'text-warning fw-bold' : ''
        } : {}
)

const getBooleanDiffCell = (text:boolean, record:IActiveCustomer, key:keyof IActiveCustomer) => {
    const res = BOOL_DROPDOWN.find(({value}:{value:boolean, label:string}) => value === text)
    return {
        props: getCellProps(record, key),
        children: res?.label
    }
}

const getRegularDiffCell = (text:string, record:IActiveCustomer, key:keyof IActiveCustomer) => ({
    props: getCellProps(record, key),
    children: text
})

const ECommerceCustomersApprovalPage:FC = () => {
	const [messageApi, contextHolder] = message.useMessage();
    const { id } = useParams<ParamTypes>();
    const dispatch = useDispatch();

    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [selectedCountry, setSelectedCountry] = useState<ICountry | null>(null);
    
    const countries = useSelector((state:any) => state.eCommCustomers.countries);
    const countriesLoading = useSelector((state:any) => state.eCommCustomers.countriesLoading);
    
    const subChannelsLoading = useSelector((state:any) => state.eCommCustomers.subChannelsLoading);
    const subChannels = useSelector((state:any) => state.eCommCustomers.subChannels);
    const additionalColumns = useSelector((state:any) => state.eCommCustomers.additionalColumns);
    const dataLoading = useSelector((state:any) => state.eCommCustomers.dataLoading);
    const data = useSelector((state:any) => state.eCommCustomers.dataForApproval);
    const success = useSelector((state:any) => state.eCommCustomers.success);
    const error = useSelector((state:any) => state.eCommCustomers.error);

    const loadEntries = (country:ICountry) => {
		if (country) {
            let params = { country_id: country?.id }
		    dispatch(loadDataForApproval(id, 1, params));
            onSelectionChange([])
		}
    }

    useEffect(() => {
        setSelectedRows([]);
    }, [data])

    useEffect(() => {
        initialDataLoad();
        return () => {dispatch(clearData())}
    }, []);
    
    useEffect(() => {
        success && messageApi.open({
            type: 'success',
            content: success,
            style: {zIndex: 10000, marginTop: 124},
            onClose: () => {
                dispatch(clearSuccess())
            }
        });
    }, [success, selectedCountry]);

    useEffect(() => {
        error && messageApi.open({
            type: 'error',
            content: error,
            style: {zIndex: 10000, marginTop: 124},
            onClose: () => {
                dispatch(clearError())
            }
        });
    }, [error]);


	useEffect(() => {
        selectedCountry && loadEntries(selectedCountry)
	}, [selectedCountry])

    const tableColumns:IColumn[] = [
        {
            dataIndex: 'status',
            title: "Action",
            width: 120,
            fixed: 'left',
            filterSearch: true,
            sorter: (a:any, b:any) => a.status.localeCompare(b.status),
            render: (text:ICustomerStatus, record:IActiveCustomer) => {
                let textColor = 'text-dark'
                if (text === 'deleted') {
                    textColor = 'text-danger'
                }
                if (text === 'updated') {
                    textColor = 'text-warning'
                }
                if (text === 'created') {
                    textColor = 'text-success'
                }
                return <div className={`fw-bold text-capitalize ${textColor}`}>{text}</div>
            }
        },
        {
            dataIndex: E_CUSTOMER_TYPE,
            title: "Type of eCustomer",
            width: 120,
            fixed: 'left',
            filterSearch: true,
            sorter: (a:any, b:any) => a[E_CUSTOMER_TYPE].localeCompare(b[E_CUSTOMER_TYPE]),
            render: (text:string, record:IActiveCustomer) => {
                const res = subChannels.find(({value}:{value:string}) => value === text)
                return {
                    props: {
                        ...(record.status === 'updated'
                            ? {
                                className: record[E_CUSTOMER_TYPE] !== record.children?.[0]?.[E_CUSTOMER_TYPE] ? 'bg-warning' : ''
                            }
                            : {})
                    },
                    children: res.label
                }
            }
        },
        {
            dataIndex: CUSTOMER_NAME,
            title: "Customer Name",
            width: 200,
            fixed: 'left',
            filterSearch: true,
            onFilter: (value:string, record:any) => record[CUSTOMER_NAME].startsWith(value),
            sorter: (a:any, b:any) => a[CUSTOMER_NAME].localeCompare(b[CUSTOMER_NAME]),
            render: (text:ICustomerStatus, record:IActiveCustomer) => getRegularDiffCell(text, record, CUSTOMER_NAME)
        },
        {
            dataIndex: PRODUCT_SUPPLIER,
            title: "Product Supplier",
            width: 260,
            onFilter: (value:string, record:any) => record[PRODUCT_SUPPLIER].startsWith(value),
            sorter: (a:any, b:any) => a[PRODUCT_SUPPLIER].localeCompare(b[PRODUCT_SUPPLIER]),
            render: (text:ICustomerStatus, record:IActiveCustomer) => getRegularDiffCell(text, record, PRODUCT_SUPPLIER)
        },
        {
            dataIndex: SAP_CODE,
            title: "CPL/Customer SAP code",
            width: 260,
            render: (text:ICustomerStatus, record:IActiveCustomer) => getRegularDiffCell(text, record, SAP_CODE)
        },
        {
            dataIndex: CODE_HIERARHY,
            title: "Code hierarchy",
            width: 260,
            render: (text:ICustomerStatus, record:IActiveCustomer) => getRegularDiffCell(text, record, CODE_HIERARHY)
        },
        {
            dataIndex: DIRECT_INDIRECT,
            title: "Direct/Indirect",
            width: 120,
            render: (text:boolean, record:IActiveCustomer) => getBooleanDiffCell(text, record, DIRECT_INDIRECT),
        },
        {
            dataIndex: SELLES_DATA_SHARING,
            title: "Sales Data sharing from customer",
		    width: 160,
            render: (text:ICustomerStatus, record:IActiveCustomer) => getRegularDiffCell(text, record, SELLES_DATA_SHARING)
        },
        {
            dataIndex: FREQUENCY_DATA_SHARING,
            title: "Frequency of Data Sharing (every week, month, bi-monthly, quarterly, semi-annualy, etc)",
            width: 360,
            render: (text:ICustomerStatus, record:IActiveCustomer) => getRegularDiffCell(text, record, FREQUENCY_DATA_SHARING)
        },
        {
            dataIndex: SELL_DATA_MESH,
            title: "Sell ot on the data mesh",
		    width: 160,
            render: (text:ICustomerStatus, record:IActiveCustomer) => getRegularDiffCell(text, record, SELL_DATA_MESH)
        },
        {
            dataIndex: DATA_REPORTING_ESTIMATION,
            title: "Data for reporting estimation list",
            width: 320,
            render: (text:ICustomerStatus, record:IActiveCustomer) => getRegularDiffCell(text, record, DATA_REPORTING_ESTIMATION)
        },
        {
            dataIndex: PRIORITY,
            title: "Priority",
            width: 100,
            render: (text:ICustomerStatus, record:IActiveCustomer) => getRegularDiffCell(text, record, PRIORITY)
        },
        {
            dataIndex: PFP_IMPLEMENTATION,
            title: "PfP implementation",
		    width: 120,
            render: (text:ICustomerStatus, record:IActiveCustomer) => getRegularDiffCell(text, record, PFP_IMPLEMENTATION)
        },
        {
            dataIndex: DESCRIBE_PLANNED,
            title: "Describe planned (with deadline)/factual PfP implemented",
		    width: 240,
            render: (text:ICustomerStatus, record:IActiveCustomer) => getRegularDiffCell(text, record, DESCRIBE_PLANNED)
        },
        {
            dataIndex: ERED_ERETAIL,
            title: "eRed for eRetail",
		    width: 140,
            render: (text:ICustomerStatus, record:IActiveCustomer) => getRegularDiffCell(text, record, ERED_ERETAIL)
        },
        {
            dataIndex: ERED_FSA,
            title: "eRed for FSA",
		    width: 140,
            render: (text:ICustomerStatus, record:IActiveCustomer) => getRegularDiffCell(text, record, ERED_FSA)
        },
        {
            dataIndex: ERED_QC,
            title: "eRed for QC",
		    width: 140,
            render: (text:ICustomerStatus, record:IActiveCustomer) => getRegularDiffCell(text, record, ERED_QC)
        },
        {
            dataIndex: ERED_QSR,
            title: "eRed for QSR",
		    width: 140,
            render: (text:ICustomerStatus, record:IActiveCustomer) => getRegularDiffCell(text, record, ERED_QSR)
        },
        {
            dataIndex: MEDIA_HOUSE,
            title: "Media House available/in house sales",
            width: 180,
            render: (text:boolean, record:IActiveCustomer) => getBooleanDiffCell(text, record, MEDIA_HOUSE),
        },
        {
            dataIndex: RETAIL_MEDIA,
            title: "Retail Media",
            width: 100,
            render: (text:boolean, record:IActiveCustomer) => getBooleanDiffCell(text, record, RETAIL_MEDIA),
        },
        {
            dataIndex: MARKET_SHARES,
            title: "Market shares data sharing",
		    width: 160,
            render: (text:boolean, record:IActiveCustomer) => getBooleanDiffCell(text, record, MARKET_SHARES),
        },
        {
            dataIndex: CUSTOMER_URL,
            title: "Customer URL",
		    width: 260,
            render: (text:ICustomerStatus, record:IActiveCustomer) => getRegularDiffCell(text, record, CUSTOMER_URL)
        },
        {
            dataIndex: COLLABORATIVE_ADS,
            title: "Collaborative ads",
            width: 160,
            render: (text:boolean, record:IActiveCustomer) => getBooleanDiffCell(text, record, COLLABORATIVE_ADS),
        },
        {
            dataIndex: ONSITE_RETAIL_MEDIA,
            title: "Onsite retail media available",
            width: 160,
            render: (text:boolean, record:IActiveCustomer) => getBooleanDiffCell(text, record, ONSITE_RETAIL_MEDIA)
        },
        {
            dataIndex: SHARED_DATA_WITH_CUSTOMERS,
            title: "If Y, which data can be shared by customers from the below list? ( Sales uplift, Market Share, New to Brand, Basket Incidence, Repeat Purchase, ROAs)",
            width: 260,
            render: (text:ICustomerStatus, record:IActiveCustomer) => getRegularDiffCell(text, record, SHARED_DATA_WITH_CUSTOMERS)
        },
        {
            dataIndex: DIGITAL_TOOLS_OFFERED,
            title: "If Y which digital tools are offered to suppliers for RM activation?",
            width: 260,
            render: (text:ICustomerStatus, record:IActiveCustomer) => getRegularDiffCell(text, record, DIGITAL_TOOLS_OFFERED)
        },
        {
            dataIndex: ORDERING_CHANNEL,
            title: "Ordering channel (EDI, Direct, CP, WHS)",
            width: 260,
            render: (text:ICustomerStatus, record:IActiveCustomer) => getRegularDiffCell(text, record, ORDERING_CHANNEL)
        },
        ...additionalColumns.map((col:IColumn) => ({
            ...col,
            render: (text:ICustomerStatus, record:IActiveCustomer) => getRegularDiffCell(text, record, col.dataIndex as keyof IActiveCustomer)
        })),
        {
            dataIndex: 'created_by',
            title: "Created by",
            width: 180,
            filterSearch: true,
            sorter: (a:any, b:any) => a.created_by.localeCompare(b.created_by),
        },
        {
            dataIndex: 'created_at',
            title: "Created at",
            width: 180,
            render: (text:string) => moment(text)?.tz("CET").format("DD MMM YYYY HH:mm z") 
        },
        {
            dataIndex: 'updated_by',
            title: "Updated by",
            width: 180,
            filterSearch: true,
            sorter: (a:any, b:any) => a.created_at.localeCompare(b.created_at),
        },
        {
            dataIndex: 'updated_at',
            title: "Updated at",
            width: 180,
            render: (text:string) => moment(text)?.tz("CET").format("DD MMM YYYY HH:mm z")
        },
    ].map((c) => ({
        ...c,
        minWidth: 250
    }))

    
    const initialDataLoad = async () => {
        dispatch(getColumns(id));
        dispatch(loadCountries(id));
        dispatch(loadSubChannels(id)); 	
    }

    const setDropDownValues =async (id:string, title:string ,valueType:string) => {
        if (valueType === "Country") setSelectedCountry({id, title});
    };

    const onSelectionChange = (newRows:any[]) => {
        let newSelectedRows = []
        newSelectedRows = newRows.filter((id) => {
            return data.some((item:IActiveCustomer) => item.id === id)
        })
        setSelectedRows(newSelectedRows)
    }

    const rowSelection = {
        selectedRowKeys: selectedRows,
        onChange: onSelectionChange,
    }

    const handleApprove = () => {
        let rowsForRejection:IActiveCustomer[] = [];
        let approvedRows:any = {}

        // GET APPROVED RECORDS AND GROUP THEM BY CUSTOMER NAME
        selectedRows.forEach((rowId:number) => {
            const sRow = data.find((record:IActiveCustomer) => record.id === rowId);
            if (sRow) {
                approvedRows = {
                    ...approvedRows,
                    [sRow.customer_name]: [
                        ...(approvedRows[sRow.customer_name] || []),
                        sRow
                    ]
                }
            }
        })

        // SORT THEM BY UPDATE DATE
        let repeatingUpdateRowsForDelete:number[] = []
        Object.entries(approvedRows).forEach(([ key, value ]:any) => {
            const sortedRecords = value.sort((a:IActiveCustomer, b:IActiveCustomer) =>
                (new Date(b.updated_at as string) as any) - (new Date(a.updated_at as string) as any)
            )
            approvedRows[key] = sortedRecords[approvedRows[key].length - 1]

            sortedRecords.forEach((element:IActiveCustomer, index:number) => {
                if (index < sortedRecords.length - 1) {
                    repeatingUpdateRowsForDelete.push(element.id as number)
                }
            });
        })
        
        // Get the duplicate updates for non selected rows that have the same customer name
        selectedRows.forEach((rowId:number) => {
            const sRow = data.find((record:IActiveCustomer) => record.id === rowId);

            if (sRow) {
                const { children = [] } = sRow;
                rowsForRejection = data.filter((record:IActiveCustomer) => {
                    // Check if there are duplicate records marked for deletion
                    if (children && children.length > 0) {
                        if (record.status === 'deleted' && children[0]) {
                            return children[0].customer_name === sRow.customer_name
                        } else {
                            if (record.children && record.children.length > 0) {
                                return children[0].customer_name === record.children[0].customer_name
                            }
                        }
                    }
                    return false
                })
            }
        })

        const rejectionIds:number[] = rowsForRejection
            .map(({id}) => id)
            .filter((id) =>
                !selectedRows.includes(id as number)) as number[]

        
        selectedCountry && dispatch(adminAppoveRecords(
            id,
            (Object.values(approvedRows) as IActiveCustomer[]).map(({id}) => id as number),
            selectedCountry.id,
            [...rejectionIds, ...repeatingUpdateRowsForDelete].filter((v, i, a) => a.indexOf(v) === i)
        ))
    }

    const handleReject = () => {
        selectedCountry && dispatch(adminDeleteRecords(id, selectedRows, selectedCountry.id))
    }

    return (
        <Suspense fallback={<div>An error occured!</div>}>
            {contextHolder}
            <div className="user-view-container">
                <div className="user-view-header row p-0 m-0 mb-4">
                    <div className="col-9 p-0 m-0 row">
                        <div className="col-5 p-0 m-0">
                            <DropDownCustom
                                    title="Country"
                                    options={countries}
                                    loading={countriesLoading}
                                    setParentDropdownValue={setDropDownValues}
                                />
                        </div>
                    </div>

                    <div className="col-3 p-0 m-0 d-flex align-items-center justify-content-end">
						<Button
							disabled={!selectedCountry || selectedRows.length === 0}
							onClick={handleApprove}
							type="primary"
						>
							Approve selected
						</Button>

                        <Button
							disabled={!selectedCountry || selectedRows.length === 0}
							onClick={handleReject}
							type="primary"
							className="ms-1 brand-background"
						>
							Reject selected
						</Button>
                    </div>
                </div>
                
                <Spin tip="Loading" spinning={dataLoading || subChannelsLoading} size="large">
                    {/* MAIN TABLE */}
                    <Table
                        rowKey={'id'}
                        rowSelection={rowSelection}
                        columns={tableColumns}
                        size="small"
                        dataSource={data}
						rowClassName={() => 'editable-row'}
						bordered
						scroll={{
							x: tableColumns.reduce((a:number, {width}) => a + width, 0),
							y: 570
						}}
                    />
                </Spin>
            </div>
        </Suspense>
    );
}

export default ECommerceCustomersApprovalPage;
